import { useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient } from '../../providers/auth-provider';

import { inputStyle, mono, primaryButton, secondaryButton, selectStyle } from '../proxy-vault/ui';

import type { SandboxRegistryResponse } from '../../lib/api-client';

/**
 * Register a container registry — the fleet's pull-credential intake.
 * Registry-generic on purpose: Docker Hub, ACR, GHCR, anything; several are
 * active at once and each bundle's image host picks its match. The secret is
 * write-only — after Create it exists as a fingerprint and a pull secret,
 * never as text again.
 */
export function AddRegistryModal({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (registry: SandboxRegistryResponse) => void;
}) {
  const api = useApiClient();
  const [name, setName] = useState('');
  const [loginServer, setLoginServer] = useState('');
  const [authType, setAuthType] = useState<'token' | 'username_password' | 'cluster_identity'>('token');
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const secretless = authType === 'cluster_identity';

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.createSandboxRegistry({
        name: name.trim(),
        login_server: loginServer.trim().toLowerCase(),
        auth_type: authType,
        ...(!secretless && username.trim() ? { username: username.trim() } : {}),
        ...(secretless ? {} : { secret }),
      });
      onCreated(created);
    } catch (err) {
      setError(describeApiError(err, String(err)));
      setBusy(false);
    }
  };

  const canSubmit = name.trim() && loginServer.trim() && (secretless || secret) && !busy;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
      }}
    >
      <div
        style={{
          width: 460,
          maxWidth: '92vw',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>Register a registry</div>
        <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
          Name
          <input
            data-testid="fleet-registry-name"
            style={{ ...inputStyle, width: '100%', marginTop: 4 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="payfactory-dockerhub"
          />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
          Login server (host only — <span style={{ fontFamily: mono }}>docker.io</span>,{' '}
          <span style={{ fontFamily: mono }}>myregistry.azurecr.io</span>, …)
          <input
            data-testid="fleet-registry-host"
            style={{ ...inputStyle, width: '100%', marginTop: 4, fontFamily: mono }}
            value={loginServer}
            onChange={(e) => setLoginServer(e.target.value)}
            placeholder="docker.io"
          />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
          Auth type
          <select
            style={{ ...selectStyle, width: '100%', marginTop: 4 }}
            value={authType}
            onChange={(e) => setAuthType(e.target.value as 'token' | 'username_password' | 'cluster_identity')}
          >
            <option value="token">Token (ACR scope-map, GHCR PAT, …)</option>
            <option value="username_password">Username + password/PAT (Docker Hub, …)</option>
            <option value="cluster_identity">Cluster identity (secretless — node identity pulls)</option>
          </select>
        </label>
        {secretless ? (
          <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.5 }}>
            No credential is stored: pulls ride the cluster&apos;s own infrastructure identity (e.g. an
            AKS kubelet <span style={{ fontFamily: mono }}>AcrPull</span> role assignment). Registering
            the host keeps the supply-chain gate authoritative.
          </div>
        ) : (
          <>
            <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
              Username {authType === 'token' ? '(optional — defaults to "token")' : '(required)'}
              <input
                data-testid="fleet-registry-username"
                style={{ ...inputStyle, width: '100%', marginTop: 4, fontFamily: mono }}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
              Secret (write-only — stored server-side, never displayed again)
              <input
                data-testid="fleet-registry-secret"
                type="password"
                style={{ ...inputStyle, width: '100%', marginTop: 4, fontFamily: mono }}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
            </label>
          </>
        )}
        {error && (
          <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)' }} data-testid="fleet-registry-error">
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={secondaryButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            data-testid="fleet-registry-create"
            style={{ ...primaryButton, opacity: canSubmit ? 1 : 0.5 }}
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {busy ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
}
